import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';

import { Accordion } from '@klv/utils/accordion';
import Icon from '@klv/atoms/icon';
import { Anchor } from '@klv/atoms/anchor';
import { generateClassName } from '@klv/shared/utils/functions';

import * as styles from './styles/articleTableOfContents.module.scss';

import type { IArticleTableOfContents } from './types';

const ArticleTableOfContents = ({ className, currentSection, headingTag = 'div', sections, ...rest }: IArticleTableOfContents) => {
  const { t } = useTranslation();
  const [groupHeight, setGroupHeight] = useState(220);
  const ref = useRef<HTMLDetailsElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) {
      new Accordion(ref.current, { maxWidth: 1200 });
    }
  }, [ref]);

  //  This use effect and the groupRef are to find the height of the first 4 headings and set the scrollable container max-height
  useEffect(() => {
    if (groupRef.current) {
      if (groupRef?.current?.clientHeight > 0) {
        setGroupHeight(groupRef?.current?.clientHeight);
      }
    }
  }, [sections]);

  const firstFour = sections.slice(0, 4);
  const remainingSections = sections.slice(4);

  const renderListItems = (section: { id: string; name: string; }, index: number) => {
    return (<Anchor
      aria-current={currentSection ? (currentSection === section.id) : (index === 0)}
      className={generateClassName(['sectionLink', styles.sectionLinkToc])}
      to={`#${section.id}`}
    >
      <li className={generateClassName(['linkListItem', styles.linkListItem])} key={section.id}>
          <p
            className={generateClassName(['paragraph1', styles.sectionName])}
          >
            {section.name}
          </p>
      </li>
    </Anchor>);
  };

  return (
    <nav className={generateClassName([className, styles.articleTableOfContents])}  {...rest}>
      <details open ref={ref}>
        <summary className={styles.headerContainer}>
          <h5 className={generateClassName(['h5', 'semiBold', styles.heading])}>{t('organisms_articleTableContents_tableContents')}</h5>
          <div className={styles.chevron}>
              <Icon iconName='ChevronNorth' size={2} fillColor='currentColor' />
          </div>
        </summary>
      <ul style={{ maxHeight: `${groupHeight}px` }} className={generateClassName([styles.sectionLinkList, 'content'])}>
        <div ref={groupRef}>
          {firstFour.map((section, index) => (
            renderListItems(section, index)
          ))}
        </div>
          {remainingSections.map((section, index) => (
            renderListItems(section, index + 4)
          ))}
      </ul>
      </details>
    </nav>
  );
};

export default ArticleTableOfContents;
